// Chemin: C:\gestion-copro\src\pages\vigie\DocumentsVigiePage.tsx
// GED personnelle VIGIE — Alignée sur la structure CS (principe ESCALATOR)
// Session 100 : Placeholder
// Session 102 : Version fonctionnelle — upload, liste, filtres, suppression
// Session 103 : Redirection création vers SENTINELLE (cockpit central)
// Session 106 : REFONTE — Vue catégorisée identique au CS (DocumentCategoryAccordion)
//               Principe ESCALATOR : VIGIE = sous-ensemble du CS, même UX sur modules communs
// Session 125 : Fix — Masquer catégorie "Interne CS" côté VIGIE (accordéons + filtres + upload)
// Session 194 : Bouton header renommé "Ajouter un document" — workflow SENTINELLE explicité
// Session 195 : Bouton redirige vers /vigie/documents/ajouter (AjouterDocumentVigiePage)
//               Suppression modal upload manuel (non exposée en production — décision S194)
// Session 219 : Suppression bouton "Ajouter" — porte directe AjouterDocumentVigiePage fermée
// Session 232 : Archivage document — bouton Archive/ArchiveRestore, estArchive exclu du contexte SENTINELLE
// Session 272 : Bouton upload direct (AjouterDocumentVigiePage) + bouton SENTINELLE — deux portes d'entrée
// Session 274 : Suppression toggle Vue (Par catégorie / Liste) + filtre catégorie — redondants avec accordéons
// Session 282 : Ajout donneesCles dans DocumentData — badge statut paiement dans accordéon
// Session 256 : GED unifiée — suppression du module "Mes correspondances" séparé
//               Les courriers/emails miroirs (source=sentinelle_vigie) apparaissent maintenant
//               directement dans la GED, classés par catégorie comme tous les autres documents
// Réutilise la collection racine 'documents' avec coproprieteId = vigie-{uid}

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';

// Session 136 : Aide contextuelle 3 niveaux
import { useAideContextuelle } from '../../hooks/useAideContextuelle';
import AideContextuelleModule from '../../components/ui/AideContextuelleModule';
import { AIDE_DOCUMENTS } from '../../constants/aideContextuelle';
import {
  getTypeDocumentLabel,
  getTypeColor,
  getCategorieLabel,
  TYPES_DOCUMENT,
  deleteDocument,
  archiverDocument,
  desarchiverDocument
} from '../../services/document.service';
import { CategorieDocument } from '../../types/document.types';
import DocumentCategoryAccordion from '../../components/documents/DocumentCategoryAccordion';
import {
  FolderOpen,
  FileText,
  Search,
  X,
  Trash2,
  Download,
  Eye,
  Loader2,
  AlertCircle,
  Sparkles,
  Upload,
  Archive,
  ArchiveRestore,
} from 'lucide-react';

// ============================================
// CONSTANTES VIGIE
// ============================================

// Session 125 : Catégories à exclure côté VIGIE — "Interne CS" n'a pas de sens pour un copropriétaire isolé
const VIGIE_EXCLUDED_CATEGORIES: CategorieDocument[] = ['interne_cs'];


// ============================================
// TYPES
// ============================================


interface DocumentData {
  id: string;
  coproprieteId: string;
  titre: string;
  description?: string;
  type: string;
  categorie?: CategorieDocument;
  fichierUrl: string;
  fichierNom: string;
  fichierTaille: number;
  fichierType: string;
  dateDocument: any;
  uploadeParNom?: string;
  createdAt: any;
  visibilite?: 'cs_only' | 'public' | 'proprietaire';
  estArchive?: boolean;
  source?: string; // Session 253 : discriminant miroir ('sentinelle_vigie' = miroir IC)
  donneesCles?: Record<string, string>; // Session 282 : statut paiement, montant, date règlement
  statutCourrier?: 'brouillon' | 'finalise' | 'envoye'; // Session 294 : badge statut dans la GED
  dateEnvoiCourrier?: any; // Session 294 : date affichée dans le badge "Envoyé le XX/XX"
  sens?: 'recu' | 'envoye'; // Session 297 : badge "Reçu le" / "Envoyé le" dans la GED
}

// ============================================
// UTILITAIRES
// ============================================

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 o';
  const k = 1024;
  const sizes = ['o', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (timestamp: any): string => {
  if (!timestamp) return 'Non renseigné';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return 'Non renseigné';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getFileIcon = (fichierType: string): string => {
  if (fichierType?.includes('pdf')) return 'text-red-500';
  if (fichierType?.includes('image') || fichierType?.includes('jpg') || fichierType?.includes('png')) return 'text-green-500';
  if (fichierType?.includes('word') || fichierType?.includes('document')) return 'text-blue-500';
  if (fichierType?.includes('sheet') || fichierType?.includes('excel')) return 'text-emerald-500';
  return 'text-gray-400';
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function DocumentsVigiePage() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { coproprieteId } = useCopropriete();

  // Session 136 : Aide contextuelle
  const aide = useAideContextuelle({ moduleKey: 'documents' });

  // États liste
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // États filtres
  const [filtreType, setFiltreType] = useState('');
  const [filtreRecherche, setFiltreRecherche] = useState('');

  // États suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [archivageEnCours, setArchivageEnCours] = useState<string | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentData | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Message succès
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Auto-masquer le message de succès
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Charger les documents
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!coproprieteId) {
        setLoading(false);
        return;
      }

      try {
        const docsQuery = query(
          collection(db, 'documents'),
          where('coproprieteId', '==', coproprieteId),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(docsQuery);
        const docsData = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })) as DocumentData[];
        setDocuments(docsData);
      } catch (err: any) {
        console.error('Erreur chargement documents:', err);
        if (err.message?.includes('index')) {
          setError('Index Firestore requis. Vérifiez la console Firebase.');
        } else {
          setError('Erreur lors du chargement des documents');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [coproprieteId]);

  // Filtrer les documents
  const documentsFiltres = documents.filter(d => {
    if (filtreType && d.type !== filtreType) return false;
    if (filtreRecherche) {
      const recherche = filtreRecherche.toLowerCase();
      const matchTitre = d.titre?.toLowerCase().includes(recherche);
      const matchDescription = d.description?.toLowerCase().includes(recherche);
      if (!matchTitre && !matchDescription) return false;
    }
    return true;
  });

  const filtresActifs = [filtreType, filtreRecherche].filter(Boolean).length;
  const rechercheActive = filtreRecherche.trim().length > 0;

  const resetFiltres = () => {
    setFiltreType('');
    setFiltreRecherche('');
  };

  // Suppression
  // Session 232 : Archiver/désarchiver un document
  const handleArchiver = async (doc: DocumentData) => {
    setArchivageEnCours(doc.id);
    try {
      if (doc.estArchive) {
        await desarchiverDocument(doc.id);
      } else {
        await archiverDocument(doc.id);
      }
      // Mettre à jour localement sans recharger
      setDocuments(prev => prev.map(d =>
        d.id === doc.id ? { ...d, estArchive: !doc.estArchive } : d
      ));
    } catch (err) {
      console.error('Erreur archivage:', err);
    } finally {
      setArchivageEnCours(null);
    }
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;
    setDeleting(true);
    try {
      await deleteDocument(documentToDelete.id, documentToDelete.fichierUrl);
      setDocuments(prev => prev.filter(d => d.id !== documentToDelete.id));
      setSuccessMessage('Document supprimé');
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    } catch (err) {
      console.error('Erreur suppression:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes documents</h1>
          <p className="text-gray-600 mt-1">
            {documentsFiltres.length} document{documentsFiltres.length > 1 ? 's' : ''}
            {filtresActifs > 0 && ` (${filtresActifs} filtre${filtresActifs > 1 ? 's' : ''} actif${filtresActifs > 1 ? 's' : ''})`}
          </p>
        </div>

      </div>

      {/* Session 298 : Encart dépôt — redirige vers SENTINELLE (porte unique GED) */}
      <button
        onClick={() => navigate('/vigie/sentinelle', { state: { messagePreRempli: "J'ai reçu un nouveau document, peux-tu l'analyser ?" } })}
        className="w-full flex items-center gap-4 px-5 py-4 bg-amber-50 border-2 border-dashed border-amber-300 rounded-xl hover:bg-amber-100 hover:border-amber-400 transition-colors text-left"
      >
        <div className="relative w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shrink-0">
          <Upload className="w-5 h-5 text-white" />
          <span className="absolute inset-0 rounded-xl bg-amber-400 animate-ping opacity-40" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">Déposer un document</p>
          <p className="text-sm text-gray-500">SENTINELLE analyse, classe et archive automatiquement dans votre GED</p>
        </div>
        <svg className="w-5 h-5 text-amber-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Session 136 : Aide contextuelle */}
      {aide.aideChargee && (
        <AideContextuelleModule
          config={AIDE_DOCUMENTS}
          aideAccueilVue={aide.aideAccueilVue}
          afficherAideComplete={aide.afficherAideComplete}
          onFermer={aide.fermerAideAccueil}
          onVoirPlus={aide.ouvrirAideComplete}
        />
      )}

      {/* ============================================ */}
      {/* FILTRES                                      */}
      {/* ============================================ */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        {/* Filtres */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Recherche */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filtreRecherche}
                onChange={(e) => setFiltreRecherche(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
              {filtreRecherche && (
                <button
                  onClick={() => setFiltreRecherche('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Type */}
          <select
            value={filtreType}
            onChange={(e) => setFiltreType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="">Tous les types</option>
            {TYPES_DOCUMENT.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

        </div>

        {/* Reset filtres */}
        {filtresActifs > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={resetFiltres}
              className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Réinitialiser les filtres
            </button>
            <span className="text-sm text-gray-500">
              ({filtresActifs} filtre{filtresActifs > 1 ? 's' : ''} actif{filtresActifs > 1 ? 's' : ''})
            </span>
          </div>
        )}

        {/* Résultats de recherche */}
        {rechercheActive && documentsFiltres.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-gray-700">
                {documentsFiltres.length} résultat{documentsFiltres.length > 1 ? 's' : ''} pour "{filtreRecherche}"
              </span>
            </div>
            <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
              {documentsFiltres.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 hover:bg-white transition-colors">
                  <Link
                    to={`/vigie/documents/${d.id}`}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center bg-gray-100">
                      <FileText className={`w-4 h-4 ${getFileIcon(d.fichierType)}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 hover:text-amber-600 truncate block">
                          {d.titre}
                        </span>
                        {d.estArchive && (
                          <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                            Masqué SENTINELLE
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        {d.type && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${getTypeColor(d.type)}`}>
                            {getTypeDocumentLabel(d.type)}
                          </span>
                        )}
                        <span>·</span>
                        <span>{formatDate(d.dateDocument)}</span>
                        {d.categorie && (
                          <>
                            <span>·</span>
                            <span>{getCategorieLabel(d.categorie)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-1 ml-2">
                    {d.fichierUrl && (
                      <a
                        href={d.fichierUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-500 hover:text-amber-600 transition-colors"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleArchiver(d);
                      }}
                      disabled={archivageEnCours === d.id}
                      className={`p-1.5 transition-colors ${d.estArchive ? 'text-amber-500 hover:text-amber-700' : 'text-gray-500 hover:text-amber-500'}`}
                      title={d.estArchive ? 'Désarchiver (rendre visible à SENTINELLE)' : 'Archiver (masquer à SENTINELLE)'}
                    >
                      {d.estArchive
                        ? <ArchiveRestore className="w-4 h-4" />
                        : <Archive className="w-4 h-4" />
                      }
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setDocumentToDelete(d);
                        setShowDeleteModal(true);
                      }}
                      className="p-1.5 text-gray-500 hover:text-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* CONTENU SELON LA VUE                        */}
      {/* ============================================ */}
      {!rechercheActive && documentsFiltres.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          {documents.length === 0 ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun document</h3>
              <p className="text-gray-500 mb-6">
                Dépose tes documents dans SENTINELLE — elle les analyse, les classe et les archive automatiquement dans ta GED.
              </p>
              <button
                onClick={() => navigate('/vigie/sentinelle')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
              >
                <Sparkles className="w-4 h-4" />
                Ouvrir SENTINELLE
              </button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun résultat</h3>
              <p className="text-gray-500 mb-4">Aucun document ne correspond à vos filtres.</p>
              <button onClick={resetFiltres} className="text-amber-600 hover:text-amber-800 font-medium">
                Réinitialiser les filtres
              </button>
            </>
          )}
        </div>
      ) : !rechercheActive ? (
        /* Vue par catégorie — session 274 : seule vue disponible */
        <DocumentCategoryAccordion
          documents={documentsFiltres}
          basePath="/vigie/documents"
          excludeCategories={VIGIE_EXCLUDED_CATEGORIES}
          onArchive={(doc) => handleArchiver(doc as DocumentData)}
          archivageEnCours={archivageEnCours}
        />
      ) : null}

      {/* ============================================ */}
      {/* MODAL SUPPRESSION                           */}
      {/* ============================================ */}
      {showDeleteModal && documentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-sm font-medium text-gray-900 text-center mb-2 px-4 py-2 bg-gray-100 rounded">
                {documentToDelete.titre}
              </p>
              <p className="text-sm text-red-600 text-center mb-6">
                Cette action est irréversible. Le fichier sera définitivement supprimé.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDeleteModal(false); setDocumentToDelete(null); }}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Supprimer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
